import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const variants = product.variants && product.variants.length > 0
    ? product.variants
    : [{ label: product.size, price: product.price, in_stock: true }];
  // Default-select first in-stock variant; fall back to first
  const firstInStockIdx = Math.max(0, variants.findIndex((v) => v.in_stock !== false));
  const [selected, setSelected] = useState(firstInStockIdx);
  const variant = variants[selected];
  const soldOut = variant?.in_stock === false;
  const allSoldOut = variants.every((v) => v.in_stock === false);

  const handleAdd = () => {
    if (soldOut) return;
    addItem(
      {
        id: `${product.id}::${variant.label}`,
        name: `${product.name} — ${variant.label}`,
        price: variant.price,
        image_url: product.image_url,
        sku: product.sku,
      },
      1,
    );
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col bg-white border-[3px] border-black shadow-brut btn-press"
    >
      <div className="relative aspect-[4/3] overflow-hidden border-b-[3px] border-black bg-[#f4f4f5]">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 bg-[#00d4ff] text-black font-mono-ui text-[10px] font-bold px-2 py-1 border-[2px] border-black">
          {product.category}
        </div>
        <div className="absolute bottom-3 right-3 bg-[#ff1493] text-white font-mono-ui text-[10px] font-bold px-2 py-1 border-[2px] border-black">
          {variant.label}
        </div>
        {allSoldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="bg-black text-white font-display text-xl px-5 py-2 border-[3px] border-white -rotate-6">SOLD OUT</div>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="font-mono-ui text-[10px] text-black/50">{product.sku}</div>
        <h3 className="font-display text-xl leading-none">{product.name}</h3>
        <p className="text-sm text-black/70 leading-relaxed line-clamp-2">{product.short_description}</p>

        <div className="mt-1">
          <label className="font-mono-ui text-[9px] text-black/50 block mb-1">SELECT SIZE</label>
          <select
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
            data-testid={`variant-select-${product.id}`}
            onClick={(e) => e.stopPropagation()}
            className="w-full border-[3px] border-black px-2 py-1.5 font-mono-ui text-[11px] font-bold bg-white cursor-pointer"
          >
            {variants.map((v, i) => (
              <option key={i} value={i}>
                {v.label} — £{v.price.toFixed(2)}{v.in_stock === false ? " · SOLD OUT" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-auto flex items-center justify-between pt-3">
          <div className={`font-display text-2xl ${soldOut ? "line-through text-black/40" : ""}`}>£{variant.price.toFixed(2)}</div>
          <Link
            to={`/product/${product.id}`}
            data-testid={`view-product-${product.id}`}
            className="inline-flex items-center gap-1 font-mono-ui text-xs font-bold hover:text-[#ff1493]"
          >
            VIEW <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <button
          onClick={handleAdd}
          disabled={soldOut}
          data-testid={`add-to-cart-${product.id}`}
          className={`mt-2 w-full py-3 border-[3px] border-black shadow-brut-sm font-mono-ui text-xs font-bold ${soldOut ? "bg-black/70 text-white cursor-not-allowed" : "bg-[#ff1493] text-white btn-press"}`}
        >
          {soldOut ? "SOLD OUT" : "ADD TO CART"}
        </button>
      </div>
    </div>
  );
}
